package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.translateX
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.Plan
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * The commercial argument, made visual.
 *
 * **Crowdin, Transifex and POEditor** price against content multiplied by language count,
 * so the fourteenth language multiplies an invoice for strings that have not changed. That sentence
 * is true and forgettable. This section makes it a thing you do: add a language, watch one side grow
 * a whole row of billed cells, and watch the other side not move. The nothing is the message.
 *
 * **Do not widen this to "every competitor".** It was written that way and it was false: Lokalise
 * bills processed words with hosted words unlimited, Tolgee counts a key once regardless of how many
 * languages it is translated into, and Phrase Strings meters source words only. Verified against
 * each vendor's own pricing page on 2026-08-11. Any rewrite of the copy below inherits this
 * constraint — name the vendors, or say nothing about them.
 *
 * **Built from the two existing signatures, not a third.** Rows are labelled with the glyph
 * mosaic's scripts — the languages you are adding — and every cell is a type slug from the same
 * grammar as `LProgressBar`. `docs/BRAND.md` allows two signature elements and no more; this
 * composes them rather than inventing one.
 *
 * **It lives on `/pricing`, below the plan grid and above the comparison table.** It was the
 * landing page's centrepiece, where at fourteen languages it painted 336 cells — the largest and
 * most kinetic object on the site, and a diagram of somebody else's pricing model shown to a reader
 * who did not yet know what this one did. Here it is arithmetic for someone already comparing
 * prices, and it sits beside the sourced table that qualifies it.
 *
 * **The honesty rule.** No competitor's dollar figures appear here, because we cannot know a given
 * customer's tier and inventing one would be a lie that a prospect could check. What is shown is
 * the arithmetic, which is indisputable, and *our own* price, which comes from [Plan] so it cannot
 * drift from what the server bills. The caption does the rest.
 */

/** Nine of the mosaic's scripts. The languages being added are shown in the languages themselves. */
private val LANGUAGE_ROWS = listOf(
    "de" to "Hallo",
    "fr" to "Bonjour",
    "es" to "Hola",
    "ja" to "こんにちは",
    "ru" to "Привет",
    "zh" to "你好",
    "ar" to "مرحبا",
    "hi" to "नमस्ते",
    "ko" to "안녕",
    "pt" to "Olá",
    "it" to "Ciao",
    "nl" to "Hallo",
    "pl" to "Cześć",
    "tr" to "Merhaba",
    "el" to "Γεια",
    "sv" to "Hej",
)

/**
 * "1 language", not "1 languages".
 *
 * Internal so a test can hold it. An unconditional plural is a small bug anywhere and an
 * embarrassing one here: it would sit one section above a heading promising every CLDR category the
 * locale actually has.
 */
internal fun languageCount(count: Int): String =
    if (count == 1) "1 language" else "$count languages"

/**
 * "once", "twice", "14 times".
 *
 * The headline's punchline, so it has to read as English rather than as a template. "Word-count
 * pricing bills it 1 times" would undo the sentence it is the point of.
 */
internal fun timesBilled(count: Int): String = when (count) {
    1 -> "once"
    2 -> "twice"
    else -> "$count times"
}

/** The corpus the arithmetic runs on. */
internal const val SOURCE_WORDS = 40_000

/** The stepper's starting point. */
internal const val DEFAULT_LANGUAGES = 14
private const val CELLS_PER_ROW = 24
private const val MIN_LANGUAGES = 1
private val MAX_LANGUAGES = LANGUAGE_ROWS.size

@Composable
fun MultiplicationBlock() {
    var languages by remember { mutableStateOf(DEFAULT_LANGUAGES) }
    // How many rows were already on screen the last time this composed. Seeded with the opening
    // count, so the fourteen rows a visitor arrives to are settled rather than arriving.
    var settledLanguages by remember { mutableStateOf(DEFAULT_LANGUAGES) }
    val palette = ColorMode.current.toSitePalette()
    val hostedWords = SOURCE_WORDS * languages
    val studio = Plan.STUDIO

    // A plain column on /pricing rather than a full band on the landing page. At its default of
    // fourteen languages this paints 336 cells — the largest, busiest, most kinetic object anywhere
    // on the site, and a diagram of a competitor's pricing model. That is a fine thing to show
    // someone already comparing prices, and a strange thing to make the centrepiece of a homepage.
    Column(Modifier.fillMaxWidth().gap(Space.XXXL)) {
        Column(Modifier.maxWidth(46.cssRem).gap(Space.SM)) {
            Span(
                Modifier
                    .color(palette.textSecondary)
                    .fontSize(TypeScale.Micro)
                    .fontWeight(600)
                    .styleModifier {
                        property("letter-spacing", TypeScale.MicroTracking.toString())
                        property("text-transform", "uppercase")
                    }
                    .toAttrs(),
            ) { Text(t("marketing.multiplication.the_multiplication", "The multiplication")) }
            org.jetbrains.compose.web.dom.H2(
                Modifier
                    .displayFont()
                    .fontSize(TypeScale.Heading)
                    .fontWeight(700)
                    .lineHeight(TypeScale.DisplayLineHeight)
                    .margin(0.px)
                    .toAttrs { id("multiplication-title") },
            ) {
                // "They" used to open this sentence, and on a page that now carries the comparison
                // table directly below it that was indefensible: the table names Lokalise, which
                // bills processed words with hosted words unlimited and does not multiply by
                // language at all. An unqualified "they" sitting a screen above evidence that
                // contradicts it is the exact failure the KDoc above warns against. Naming the
                // pricing model instead of the vendors keeps the punch and stays true.
                // `{times}` rather than Kotlin interpolation, for the reason `tPlural` uses
                // `{count}`: a translation has to be able to put the multiplier where its own
                // grammar wants it, which a sentence assembled from two fixed halves cannot do.
                Text(
                    tf(
                        "marketing.multiplication.you_wrote_it_once_word_count_pricing",
                        "You wrote it once. Per-locale word pricing counts it {times}.",
                    "times" to timesBilled(languages)),
                )
            }
            P(
                Modifier
                    .color(palette.textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .margin(0.px)
                    .toAttrs(),
            ) {
                Text(
                    tf(
                        "marketing.multiplication.source_words_and_not_one_of",
                        "{words} source words across {languages} become {hosted} hosted words " +
                            "in a per-locale model. Lingvo does not charge for stored words; " +
                            "machine translation uses credits.",
                    "words" to formatThousands(SOURCE_WORDS), "languages" to languageCount(languages), "hosted" to formatThousands(hostedWords)),
                )
            }
        }

        // A stepper, not a slider. It is keyboard-accessible without any work, styles consistently
        // through the Modifier API, and reads in the product's voice — a range input styled to match
        // across engines is a great deal of CSS for a worse control.
        Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
            LButton(
                label = "−",
                onClick = { if (languages > MIN_LANGUAGES) languages-- },
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                enabled = languages > MIN_LANGUAGES,
                // The visible label is a bare math symbol, which is the whole accessible name a
                // screen reader gets — and at default punctuation verbosity it is not read at all.
                // Both sibling steppers in this codebase name their steps; this one did not.
                accessibleLabel = tr("marketing.multiplication.one_fewer_language", "One fewer language"),
            )
            Span(
                Modifier
                    .monoFont()
                    .tabularNumbers()
                    .minWidth(7.cssRem)
                    .color(palette.textPrimary)
                    .fontSize(TypeScale.BodySmall)
                    .styleModifier { property("text-align", "center") }
                    .toAttrs {
                        attr("aria-live", "polite")
                        // Atomic, so the whole phrase is re-read rather than the changed word alone.
                        attr("aria-atomic", "true")
                    },
            ) { Text(languageCount(languages)) }
            LButton(
                label = "+",
                onClick = { if (languages < MAX_LANGUAGES) languages++ },
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                enabled = languages < MAX_LANGUAGES,
                accessibleLabel = tr("marketing.multiplication.one_more_language", "One more language"),
            )
        }

        Div(MultiplicationGridStyle.toModifier().toAttrs()) {
            BillingColumn(
                title = t("marketing.multiplication.what_word_count_pricing_bills", "What word-count pricing bills"),
                figure = formatThousands(hostedWords),
                unit = "hosted words",
                caption = t("marketing.multiplication.one_row_per_language_add_a_2", "One row per language. Add a language, add a row — for strings you " +
                    "already wrote and have not touched since."),
                accent = false,
                // The only panel that overflows, so the only one that should be a tab stop.
                scrollable = true,
            ) {
                Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                    // **A row animates because the reader added it, never because the page loaded.**
                    // The entrance sat in the row's unconditional modifier, so opening `/pricing`
                    // played fourteen of them at once — self-starting motion on a public page, and
                    // outside the marketing budget entirely. Rows present at first paint are
                    // already settled; only the ones that arrive afterwards move.
                    LANGUAGE_ROWS.take(languages).forEachIndexed { index, (code, greeting) ->
                        LanguageRow(
                            code = code,
                            greeting = greeting,
                            arrivesLater = index >= settledLanguages,
                        )
                    }
                }
            }
            SideEffect { settledLanguages = languages }
            BillingColumn(
                title = t("marketing.multiplication.what_we_bill_you_for", "What we bill you for"),
                figure = "$${studio.pricePerSeat * 2}",
                unit = "a month, 2 billable seats",
                caption = t("marketing.multiplication.two_people_can_change_the_workspace_2", "Two people can change the workspace, so two people are billed. " +
                    "Translators and viewers are free, and there is no limit on either."),
                accent = true,
                scrollable = false,
            ) {
                Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                    repeat(2) { index ->
                        SeatRow(label = if (index == 0) "owner" else "admin")
                    }
                }
            }
        }
    }
}

@Composable
private fun BillingColumn(
    title: String,
    figure: String,
    unit: String,
    caption: String,
    accent: Boolean,
    scrollable: Boolean,
    content: @Composable () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    Div(
        MultiplicationPanelStyle.toModifier()
            .then(if (accent) SeatPanelStyle.toModifier() else BilledPanelStyle.toModifier())
            .border(1.px, LineStyle.Solid, if (accent) palette.accent else palette.border)
            .toAttrs {
                if (scrollable) {
                    attr("role", "region")
                    attr("aria-label", title)
                    tabIndex(0)
                }
            },
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            Span(
                Modifier
                    .color(palette.textSecondary)
                    .fontSize(TypeScale.Micro)
                    .fontWeight(600)
                    .styleModifier {
                        property("letter-spacing", TypeScale.MicroTracking.toString())
                        property("text-transform", "uppercase")
                    }
                    .toAttrs(),
            ) { Text(title) }
            Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.Bottom) {
                Span(
                    Modifier
                        .displayFont()
                        .tabularNumbers()
                        .color(if (accent) palette.accent else palette.textPrimary)
                        .fontSize(TypeScale.Display)
                        .fontWeight(700)
                        .lineHeight(1)
                        .styleModifier { property("letter-spacing", "-0.03em") }
                        .toAttrs(),
                ) { Text(figure) }
                Span(
                    Modifier
                        .color(palette.textSecondary)
                        .fontSize(TypeScale.Small)
                        .lineHeight(1.4)
                        .styleModifier { property("padding-bottom", "0.35rem") }
                        .toAttrs(),
                ) { Text(unit) }
            }
            content()
            Span(
                Modifier
                    // textSecondary, not textMuted: at 13px this is normal text needing 4.5:1, and
                    // textMuted clears that on no surface in either mode.
                    .color(palette.textSecondary)
                    .fontSize(TypeScale.Small)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .toAttrs(),
            ) { Text(caption) }
        }
    }
}

/** One language: its code, its own greeting, and a run of billed cells. */
@Composable
private fun LanguageRow(code: String, greeting: String, arrivesLater: Boolean) {
    val palette = ColorMode.current.toSitePalette()
    // Decided once for this row's lifetime. Recomputing it on a later recomposition would strip the
    // animation off an element still playing it.
    val entering = remember { arrivesLater }
    Row(
        Modifier
            .fillMaxWidth()
            .gap(Space.SM)
            .then(
                if (entering) {
                    Modifier.animation(
                        BilledRowEnter.toAnimation(
                            colorMode = ColorMode.current,
                            duration = Motion.StandardMs.ms,
                            timingFunction = Motion.EaseOutTiming,
                            fillMode = AnimationFillMode.Forwards,
                        ),
                    )
                } else {
                    Modifier
                },
            ),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Span(
            Modifier
                .monoFont()
                .minWidth(1.6.cssRem)
                .color(palette.textSecondary)
                .fontSize(TypeScale.Micro)
                .toAttrs(),
        ) { Text(code) }
        Span(
            Modifier
                .minWidth(4.5.cssRem)
                .color(palette.textSecondary)
                .fontSize(TypeScale.Micro)
                // Tagged so it is not read in an English voice — the product is about languages,
                // and mispronouncing them on its own landing page would be a poor advertisement.
                .toAttrs { attr("lang", code) },
        ) { Text(greeting) }
        // 24 empty cells per row carry nothing a screen reader can use, and at this contrast they
        // are not conveying information on their own either.
        Row(
            Modifier.gap(2.px).styleModifier { property("flex-wrap", "nowrap") },
            
        ) {
            repeat(CELLS_PER_ROW) {
                Div(BilledCellStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
            }
        }
    }
}

@Composable
private fun SeatRow(label: String) {
    val palette = ColorMode.current.toSitePalette()
    Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
        Span(
            Modifier
                .monoFont()
                .minWidth(6.1.cssRem)
                .color(palette.textSecondary)
                .fontSize(TypeScale.Micro)
                .toAttrs(),
        ) { Text(label) }
        Row(Modifier.gap(2.px)) {
            repeat(1) { Div(SeatCellStyle.toModifier().toAttrs()) }
        }
    }
}

val MultiplicationGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.LG)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("align-items", "start")
            }
    }
    Breakpoint.LG {
        Modifier.styleModifier { property("grid-template-columns", "1.35fr minmax(0, 1fr)") }
    }
}

val MultiplicationPanelStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .borderRadius(Radius.Card)
        .padding(Space.XL)
        .styleModifier { property("overflow-x", "auto") }
}

/** The growing side: sunken, so the rows read as filling a container rather than floating on one. */
val BilledPanelStyle = CssStyle.base {
    Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken)
}

/** The unchanging side: raised and calm, which is the entire argument in one surface choice. */
val SeatPanelStyle = CssStyle.base {
    Modifier.backgroundColor(colorMode.toSitePalette().surface)
}

/**
 * A row of billed cells arriving.
 *
 * Transform only, like every other entrance here. An earlier version faded opacity on the argument
 * that a row only appears when someone presses the stepper — but the stepper starts at 14, so
 * fourteen rows animate at first paint with no delay, which makes `opacity: 0` the applied style at
 * t=0. A suspended animation clock would then hide the language labels carrying the whole per-word
 * argument. The premise was wrong, and the rule holds without exception: an entrance must never be
 * able to hide content.
 */
val BilledRowEnter = Keyframes {
    from { Modifier.translateX((-6).px) }
    to { Modifier.translateX(0.px) }
}

/** A type slug: one billed unit, in the same grammar as segmented progress. */
val BilledCellStyle = CssStyle.base {
    Modifier
        .width(9.px)
        .height(14.px)
        .borderRadius(2.px)
        .backgroundColor(colorMode.toSitePalette().borderStrong)
        .transitionColors()
}

val SeatCellStyle = CssStyle.base {
    Modifier
        .width(9.px)
        .height(14.px)
        .borderRadius(2.px)
        .backgroundColor(colorMode.toSitePalette().accent)
}
